/**
 * Reviews (reviews.js)
 * -----------------------------------------------------------------------------
 * Hand-written theme asset. Unlike the rest of assets/ this is NOT Canvas build
 * output, so edit it here.
 *
 * Canvas renders the product info column from Vue, so the star rating badge
 * cannot be put above the product title from Liquid. sections/main-product
 * renders it into a hidden holder beside <main-product> instead; this script
 * moves it into the .star-rating element Vue creates, makes a click on it
 * scroll to the full reviews widget, and then asks the reviews app to re-scan
 * the DOM, because both apps scan once on page load and Vue re-renders the page
 * after that.
 *
 * Re-init calls:
 * - Stamped: StampedFn.reloadUGC(), which Stamped documents for AJAX/
 *   dynamically loaded content.
 *   https://stampedsupport.stamped.io/hc/en-us/articles/10152583826331
 * - Judge.me documents no equivalent. jdgm.initializeWidgets() is the public
 *   function its own boot code calls, and it re-queries the document rather
 *   than using the caches jdgm built on DOMContentLoaded, so it is safe to call
 *   again. Widgets it has already set up are skipped.
 */
(function () {
  'use strict'

  var HOLDER_SELECTOR = '[js-reviews-badge-holder]'
  var INTERVAL = 100
  var ROOT_SELECTOR = '#vue-element'
  /* The loading state renders its own copy, which Vue throws away. */
  var TARGET_SELECTOR = '.star-rating:not(.star-rating--loading)'
  var TIMEOUT = 15000
  var WIDGET_SELECTOR = '#product-reviews'

  var provider = getProvider()

  if (!provider) {
    return
  }

  /**
   * Read the provider off this script tag (set by snippets/reviews-scripts).
   *
   * @returns {String|Boolean}
   */
  function getProvider() {
    var script = document.querySelector('script[data-reviews-provider]')
    var value = script ? script.getAttribute('data-reviews-provider') : ''

    return value && value !== 'none' ? value : false
  }

  /**
   * Vue owns #vue-element. In the theme editor there is a hidden duplicate of
   * the whole page (#vue-design-mode-element), so never query the document.
   *
   * @returns {Element|Document}
   */
  function getRoot() {
    return document.querySelector(ROOT_SELECTOR) || document
  }

  /**
   * Scroll to the full reviews widget at the bottom of the page.
   *
   * @param {Event} event - Click event on the badge.
   */
  function handleBadgeClick(event) {
    var widget = document.querySelector(WIDGET_SELECTOR)

    if (!widget) {
      return
    }

    event.preventDefault()
    widget.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  /**
   * Judge.me's badge metafield ships with the badge hidden and un-hides it from
   * its own JS, which ran against the pre-Vue DOM. Redo it here for the copy
   * that ends up on the page. Products without reviews stay hidden, which is
   * what the app's own `hide_badge_preview_if_no_reviews` setting does.
   *
   * @param {Element} badge - Badge wrapper that was moved into place.
   */
  function revealJudgemeBadge(badge) {
    if (provider !== 'judgeme') {
      return
    }

    var inner = badge.querySelector('.jdgm-prev-badge')
    var reviews = inner ? parseInt(inner.getAttribute('data-number-of-reviews'), 10) : 0

    if (inner && reviews > 0) {
      inner.style.removeProperty('display')
    }
  }

  /**
   * Move the badge out of its holder and into the element Vue renders above the
   * product title.
   *
   * @returns {Boolean} - False while Vue has not rendered .star-rating yet.
   */
  function placeBadge() {
    var root = getRoot()
    var holder = root.querySelector(HOLDER_SELECTOR)
    var target = root.querySelector(TARGET_SELECTOR)

    if (!holder || !target || !holder.firstChild) {
      return false
    }

    var badge = document.createElement('div')
    badge.className = 'reviews-badge'

    while (holder.firstChild) {
      badge.appendChild(holder.firstChild)
    }

    revealJudgemeBadge(badge)
    badge.addEventListener('click', handleBadgeClick)

    /**
     * The holder itself is left in place (empty and hidden) so Vue still finds
     * the node it rendered if it patches this section again.
     */
    target.appendChild(badge)

    return true
  }

  /**
   * Tell the reviews app to look at the page again.
   *
   * @returns {Boolean} - False while the app's script has not loaded yet.
   */
  function reinitialise() {
    try {
      if (provider === 'stamped') {
        if (window.StampedFn && typeof window.StampedFn.reloadUGC === 'function') {
          window.StampedFn.reloadUGC()
          return true
        }

        return false
      }

      if (provider === 'judgeme') {
        if (window.jdgm && typeof window.jdgm.initializeWidgets === 'function') {
          window.jdgm.initializeWidgets()
          return true
        }

        return false
      }
    } catch (error) {
      /**
       * An app that is still booting must never take the product page with it.
       */
      return false
    }

    return false
  }

  /**
   * Run `task` every INTERVAL until it reports it is done, or TIMEOUT passes.
   *
   * @param {Function} task - Returns true when there is nothing left to do.
   */
  function poll(task) {
    if (task()) {
      return
    }

    var elapsed = 0

    var timer = window.setInterval(function () {
      elapsed += INTERVAL

      if (task() || elapsed >= TIMEOUT) {
        window.clearInterval(timer)
      }
    }, INTERVAL)
  }

  /**
   * Run `callback` once Canvas has booted Vue.
   *
   * @param {Function} callback - Callback to run.
   */
  function whenStarted(callback) {
    if (window.cnvs && window.cnvs.started) {
      callback()
      return
    }

    document.addEventListener('cnvs:start', callback, { once: true })
  }

  whenStarted(function () {
    var placed = false

    poll(function () {
      if (!placed) {
        placed = placeBadge()
      }

      /**
       * Keep trying until the app's script has loaded and taken the nudge, so
       * the order of the two async scripts does not matter.
       */
      return placed && reinitialise()
    })
  })
})()
